import { useEffect, useMemo, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import ScannerInput from "@/components/ScannerInput";
import ColumnFilter, { applyColumnFilters, dynamicUniques, SortableHeader, sortRows } from "@/components/ColumnFilter";
import MovementHistoryModal from "@/components/MovementHistoryModal";
import { MagnifyingGlass, Download, Buildings, Truck, Barcode, X as XIcon, ArrowDown, ArrowUp, ArrowUUpLeft, ArrowsLeftRight } from "@phosphor-icons/react";

const EMPTY_FILTERS = {
  sku: new Set(),
  manufacturer_code: new Set(),
  name: new Set(),
  serial_number: new Set(),
  supplier_name: new Set(),
  category_name: new Set(),
  warehouse_name: new Set(),
  stock: new Set(),
  unit_of_measure: new Set(),
  min_stock: new Set(),
  price: new Set(),
  value: new Set(),
};

export default function Inventory() {
  const [rows, setRows] = useState([]);
  const [byWarehouse, setByWarehouse] = useState([]);
  const [q, setQ] = useState("");
  const [whFilter, setWhFilter] = useState("");
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState(null);
  const [historyModal, setHistoryModal] = useState(null);
  const [tilesCollapsed, setTilesCollapsed] = useState(() => localStorage.getItem("inv-tiles-collapsed") === "1");
  const [physCollapsed, setPhysCollapsed] = useState(() => localStorage.getItem("inv-phys-collapsed") === "1");
  const [mobCollapsed, setMobCollapsed] = useState(() => localStorage.getItem("inv-mob-collapsed") === "1");
  const toggleTiles = () => {
    const next = !tilesCollapsed;
    setTilesCollapsed(next);
    localStorage.setItem("inv-tiles-collapsed", next ? "1" : "0");
  };
  const togglePhys = () => {
    const next = !physCollapsed;
    setPhysCollapsed(next);
    localStorage.setItem("inv-phys-collapsed", next ? "1" : "0");
  };
  const toggleMob = () => {
    const next = !mobCollapsed;
    setMobCollapsed(next);
    localStorage.setItem("inv-mob-collapsed", next ? "1" : "0");
  };

  const doScan = async (code) => {
    setScanLoading(true);
    setScanResult(null);
    try {
      const { data } = await api.get(`/serials/${encodeURIComponent(code)}/history`);
      setScanResult({ code, ...data });
    } catch (e) {
      setScanResult({ code, error: formatApiError(e) });
    } finally {
      setScanLoading(false);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/inventory/unified", { params: whFilter ? { warehouse_id: whFilter } : {} });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const loadByWarehouse = async () => {
    try {
      const { data } = await api.get("/inventory/by-warehouse");
      setByWarehouse(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Mount once: load the warehouse tiles (do not depend on whFilter).
  useEffect(() => { loadByWarehouse(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Re-run only the unified-inventory query when the warehouse filter changes.
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [whFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/export/inventory`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "giacenza.xlsx"; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Errore export"); }
  };

  const setColFilter = (key) => (v) => setFilters((f) => ({ ...f, [key]: v }));
  const resetFilters = () => { setFilters(EMPTY_FILTERS); setQ(""); setSort(null); };

  const accessors = useMemo(() => ({
    sku: (r) => r.sku || "",
    manufacturer_code: (r) => r.manufacturer_code || "",
    name: (r) => r.name || "",
    serial_number: (r) => r.serial_number || "",
    supplier_name: (r) => r.supplier_name || "",
    category_name: (r) => r.category_name || "",
    warehouse_name: (r) => r.warehouse_name || "",
    stock: (r) => Number(r.stock || 0),
    unit_of_measure: (r) => r.unit_of_measure || "pz",
    min_stock: (r) => Number(r.min_stock || 0),
    price: (r) => Number(r.price || 0),
    value: (r) => Number(r.value || 0),
  }), []);

  const filteredRows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const base = !needle ? rows : rows.filter((r) => {
      return (r.sku || "").toLowerCase().includes(needle)
        || (r.name || "").toLowerCase().includes(needle)
        || (r.manufacturer_code || "").toLowerCase().includes(needle)
        || (r.serial_number || "").toLowerCase().includes(needle);
    });
    const filt = applyColumnFilters(base, filters, accessors);
    return sortRows(filt, sort, accessors);
  }, [rows, q, filters, sort, accessors]);

  const uniques = useMemo(
    () => dynamicUniques(rows, filters, accessors),
    [rows, filters, accessors]
  );

  const PIECE_UNITS = new Set(["", "pz", "pz.", "n", "nr", "cad", "pcs"]);
  const toStockUnit = (r) => {
    const u = (r.unit_of_measure || "").trim().toLowerCase();
    const q = Number(r.stock || 0);
    if (PIECE_UNITS.has(u)) return q;
    return q > 0 ? 1 : 0;
  };
  const totals = useMemo(() => ({
    stock: filteredRows.reduce((a, b) => a + toStockUnit(b), 0),
    value: filteredRows.reduce((a, b) => a + Number(b.value || 0), 0),
  }), [filteredRows]);

  const totalStockAllWh = useMemo(
    () => byWarehouse.reduce((a, b) => a + b.stock, 0),
    [byWarehouse]
  );

  const hasActiveFilters = q || sort || Object.values(filters).some((s) => s.size > 0);

  const scrollWrapCls = "bg-white border border-neutral-200 overflow-auto max-h-[calc(100vh-320px)]";

  return (
    <div>
      <PageHeader
        title="Giacenza"
        subtitle="Situazione stock unificata: serializzati e sfusi, con ricerca multi-campo e filtri per colonna"
        action={
          <div className="flex gap-2">
            <button data-testid="inv-scanner-btn" onClick={() => { setScannerOpen(true); setScanResult(null); }} className="text-sm px-4 py-2.5 bg-[#002FA7] hover:bg-[#002480] text-white transition-colors flex items-center gap-2">
              <Barcode size={16} weight="bold" /> <span className="hidden sm:inline">Scanner</span>
            </button>
            <button data-testid="inv-export-btn" onClick={download} className="text-sm px-4 py-2.5 border border-neutral-300 bg-white hover:bg-neutral-50 transition-colors flex items-center gap-2">
              <Download size={16} /> <span className="hidden sm:inline">XLSX</span>
            </button>
          </div>
        }
      />
      <div className="p-4 md:p-8 space-y-4">
        {/* Toggle pill: tile vs dropdown compatto */}
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs uppercase tracking-wider font-semibold text-neutral-500">Filtro magazzino</div>
          <button
            data-testid="inv-toggle-tiles"
            onClick={toggleTiles}
            className="inline-flex items-center gap-1.5 px-3 py-1 border border-neutral-300 bg-white hover:bg-neutral-50 text-[11px] uppercase tracking-wider text-neutral-700 rounded-full transition-colors"
            title={tilesCollapsed ? "Mostra tile magazzini" : "Nascondi tile, usa il menu a tendina"}
          >
            {tilesCollapsed
              ? (<><ArrowDown size={12} weight="bold" /> Mostra tile</>)
              : (<><ArrowUp size={12} weight="bold" /> Comprimi tile</>)}
          </button>
        </div>

        {tilesCollapsed ? (
          <div className="flex items-center gap-2">
            <span className="label-eyebrow text-neutral-500">Filtra per magazzino:</span>
            <select
              data-testid="inv-wh-dropdown"
              value={whFilter}
              onChange={(e) => setWhFilter(e.target.value)}
              className="flex-1 md:flex-none md:min-w-[280px] px-3 py-1.5 text-sm border border-neutral-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
            >
              <option value="">Tutti i magazzini ({totalStockAllWh} pz)</option>
              {byWarehouse.map((w) => (
                <option key={w.warehouse_id} value={w.warehouse_id}>
                  {w.type === "mobile" ? "🚚" : "🏢"} {w.name} — {w.stock} pz
                </option>
              ))}
            </select>
          </div>
        ) : (
          (() => {
            const physical = byWarehouse.filter((w) => w.type !== "mobile");
            const mobile = byWarehouse.filter((w) => w.type === "mobile");
            const Tile = ({ w }) => {
              const isPhysical = w.type !== "mobile";
              const Icon = isPhysical ? Buildings : Truck;
              const active = whFilter === w.warehouse_id;
              const accent = isPhysical ? "bg-[#002FA7]" : "bg-amber-500";
              const accentText = isPhysical ? "text-[#002FA7]" : "text-amber-600";
              return (
                <button
                  key={w.warehouse_id}
                  data-testid={`wh-filter-${w.warehouse_id}`}
                  onClick={() => setWhFilter(w.warehouse_id)}
                  title={w.name}
                  className={`relative text-left pl-3 pr-2 py-1.5 border transition-colors flex items-center gap-2 ${active ? `border-transparent ${accent} text-white` : "border-neutral-200 bg-white hover:bg-neutral-50"}`}
                >
                  {!active && <span className={`absolute left-0 top-0 bottom-0 w-1 ${accent}`} />}
                  <Icon size={18} weight={active ? "fill" : "duotone"} className={active ? "text-white shrink-0" : `${accentText} shrink-0`} />
                  <div className="min-w-0 flex-1">
                    <div className={`text-[10px] uppercase tracking-wider font-semibold leading-tight truncate ${active ? "text-white/80" : "text-neutral-500"}`}>{w.name}</div>
                    <div className="font-display font-black text-base tracking-tighter leading-none mt-0.5">{w.stock}</div>
                  </div>
                </button>
              );
            };
            // Columns proportional to item counts: 1 physical + 13 mobile → 1fr : 13fr.
            // Each section still has a readable minimum width so a single
            // physical warehouse isn't squeezed into a sliver.
            // When a section is collapsed we drop its min width + weight so
            // the other section expands to fill the row.
            const phN = physCollapsed ? 1 : Math.max(physical.length, 1);
            const moN = mobCollapsed ? 1 : Math.max(mobile.length, 1);
            const phMin = physCollapsed ? "0" : "240px";
            const moMin = mobCollapsed ? "0" : "240px";
            const gridStyle = (physical.length > 0 && mobile.length > 0)
              ? { gridTemplateColumns: `minmax(${phMin}, ${phN}fr) minmax(${moMin}, ${moN}fr)` }
              : undefined;
            const SectionHeader = ({ Icon, label, count, color, borderColor, collapsed, onToggle, testid }) => (
              <button
                type="button"
                data-testid={testid}
                onClick={onToggle}
                className={`w-full flex items-center gap-2 mb-2 pb-1 border-b-2 ${borderColor} hover:opacity-80 transition-opacity`}
              >
                <Icon size={16} weight="fill" className={color} />
                <h3 className={`text-xs uppercase tracking-wider font-bold ${color}`}>{label}</h3>
                <span className="text-[10px] text-neutral-400 ml-auto">{count}</span>
                {collapsed
                  ? <ArrowDown size={12} weight="bold" className="text-neutral-500" />
                  : <ArrowUp size={12} weight="bold" className="text-neutral-500" />}
              </button>
            );
            return (
              <div className="space-y-3">
                <button
                  data-testid="wh-filter-all"
                  onClick={() => setWhFilter("")}
                  className={`w-full md:w-auto md:min-w-[220px] text-left px-4 py-2 border transition-colors flex items-center gap-3 ${!whFilter ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-neutral-200 bg-white hover:bg-neutral-50"}`}
                >
                  <div className={`text-[10px] uppercase tracking-wider font-semibold leading-tight ${!whFilter ? "text-white/80" : "text-neutral-500"}`}>Tutti i magazzini</div>
                  <div className="font-display font-black text-xl tracking-tighter leading-none ml-auto">{totalStockAllWh}</div>
                </button>
                <div
                  className={`grid gap-4 ${(physical.length > 0 && mobile.length > 0) ? "grid-cols-1 lg:grid-cols-[auto_auto]" : "grid-cols-1"}`}
                  style={gridStyle}
                >
                  {physical.length > 0 && (
                    <section className="min-w-0">
                      <SectionHeader
                        Icon={Buildings} label="Magazzini fisici" count={physical.length}
                        color="text-[#002FA7]" borderColor="border-[#002FA7]"
                        collapsed={physCollapsed} onToggle={togglePhys}
                        testid="wh-group-physical-toggle"
                      />
                      {!physCollapsed && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-1.5">
                          {physical.map((w) => <Tile key={w.warehouse_id} w={w} />)}
                        </div>
                      )}
                    </section>
                  )}
                  {mobile.length > 0 && (
                    <section className="min-w-0">
                      <SectionHeader
                        Icon={Truck} label="Furgoni tecnici" count={mobile.length}
                        color="text-amber-700" borderColor="border-amber-500"
                        collapsed={mobCollapsed} onToggle={toggleMob}
                        testid="wh-group-mobile-toggle"
                      />
                      {!mobCollapsed && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-1.5">
                          {mobile.map((w) => <Tile key={w.warehouse_id} w={w} />)}
                        </div>
                      )}
                    </section>
                  )}
                </div>
              </div>
            );
          })()
        )}

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-white border border-neutral-200 px-3 py-2 flex-1 min-w-[200px] max-w-md">
            <MagnifyingGlass size={16} className="text-neutral-400" />
            <input
              data-testid="inventory-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca SKU, prodotto, cod. produttore o seriale..."
              className="flex-1 outline-none text-sm bg-transparent"
            />
          </div>
          {hasActiveFilters && (
            <button data-testid="inv-reset-filters" onClick={resetFilters} className="px-3 py-2 border border-neutral-300 text-xs uppercase tracking-wider hover:bg-neutral-50">
              Reset filtri
            </button>
          )}
          <div className="text-xs text-neutral-500 ml-auto" data-testid="inv-row-count">
            {filteredRows.length} {filteredRows.length === 1 ? "riga" : "righe"} {hasActiveFilters ? "(filtrate)" : ""}
          </div>
        </div>

        <div className={scrollWrapCls}>
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200 sticky top-0 z-20 shadow-sm">
              <tr>
                <th className="px-3 py-3 label-eyebrow text-center bg-neutral-50">
                  <span className="inline-flex items-center gap-1 justify-center w-full">
                    <SortableHeader testid="sort-sku" label="SKU" sortKey="sku" sortConfig={sort} setSortConfig={setSort} />
                    <ColumnFilter testid="filter-sku" values={uniques.sku} selected={filters.sku} onChange={setColFilter("sku")} />
                  </span>
                </th>
                <th className="px-3 py-3 label-eyebrow text-center bg-neutral-50">
                  <span className="inline-flex items-center gap-1 justify-center w-full">
                    <SortableHeader testid="sort-mc" label="Cod. Produttore" sortKey="manufacturer_code" sortConfig={sort} setSortConfig={setSort} />
                    <ColumnFilter testid="filter-mc" values={uniques.manufacturer_code} selected={filters.manufacturer_code} onChange={setColFilter("manufacturer_code")} />
                  </span>
                </th>
                <th className="px-3 py-3 label-eyebrow text-center bg-neutral-50">
                  <span className="inline-flex items-center gap-1 justify-center w-full">
                    <SortableHeader testid="sort-name" label="Prodotto" sortKey="name" sortConfig={sort} setSortConfig={setSort} />
                    <ColumnFilter testid="filter-name" values={uniques.name} selected={filters.name} onChange={setColFilter("name")} />
                  </span>
                </th>
                <th className="px-3 py-3 label-eyebrow text-center bg-neutral-50">
                  <span className="inline-flex items-center gap-1 justify-center w-full">
                    <SortableHeader testid="sort-serial" label="Seriale" sortKey="serial_number" sortConfig={sort} setSortConfig={setSort} />
                    <ColumnFilter testid="filter-serial" values={uniques.serial_number} selected={filters.serial_number} onChange={setColFilter("serial_number")} />
                  </span>
                </th>
                <th className="px-3 py-3 label-eyebrow text-center bg-neutral-50">
                  <span className="inline-flex items-center gap-1 justify-center w-full">
                    <SortableHeader testid="sort-category" label="Categoria" sortKey="category_name" sortConfig={sort} setSortConfig={setSort} />
                    <ColumnFilter testid="filter-category" values={uniques.category_name} selected={filters.category_name} onChange={setColFilter("category_name")} />
                  </span>
                </th>
                <th className="px-3 py-3 label-eyebrow text-center bg-neutral-50">
                  <span className="inline-flex items-center gap-1 justify-center w-full">
                    <SortableHeader testid="sort-supplier" label="Fornitore" sortKey="supplier_name" sortConfig={sort} setSortConfig={setSort} />
                    <ColumnFilter testid="filter-supplier" values={uniques.supplier_name} selected={filters.supplier_name} onChange={setColFilter("supplier_name")} />
                  </span>
                </th>
                <th className="px-3 py-3 label-eyebrow text-center bg-neutral-50">
                  <span className="inline-flex items-center gap-1 justify-center w-full">
                    <SortableHeader testid="sort-wh" label="Magazzino" sortKey="warehouse_name" sortConfig={sort} setSortConfig={setSort} />
                    <ColumnFilter testid="filter-wh" values={uniques.warehouse_name} selected={filters.warehouse_name} onChange={setColFilter("warehouse_name")} />
                  </span>
                </th>
                <th className="px-3 py-3 label-eyebrow text-center bg-neutral-50">
                  <span className="inline-flex items-center gap-1 justify-center w-full">
                    <SortableHeader testid="sort-stock" label="Giac." sortKey="stock" sortConfig={sort} setSortConfig={setSort} />
                    <ColumnFilter testid="filter-stock" values={uniques.stock} selected={filters.stock} onChange={setColFilter("stock")} />
                  </span>
                </th>
                <th className="px-3 py-3 label-eyebrow text-center bg-neutral-50">
                  <span className="inline-flex items-center gap-1 justify-center w-full">
                    <SortableHeader testid="sort-uom" label="U.M." sortKey="unit_of_measure" sortConfig={sort} setSortConfig={setSort} />
                    <ColumnFilter testid="filter-uom" values={uniques.unit_of_measure} selected={filters.unit_of_measure} onChange={setColFilter("unit_of_measure")} />
                  </span>
                </th>
                <th className="px-3 py-3 label-eyebrow text-center bg-neutral-50">
                  <span className="inline-flex items-center gap-1 justify-center w-full">
                    <SortableHeader testid="sort-min" label="Scorta Min." sortKey="min_stock" sortConfig={sort} setSortConfig={setSort} />
                    <ColumnFilter testid="filter-min" values={uniques.min_stock} selected={filters.min_stock} onChange={setColFilter("min_stock")} />
                  </span>
                </th>
                <th className="px-3 py-3 label-eyebrow text-center bg-neutral-50">
                  <span className="inline-flex items-center gap-1 justify-center w-full">
                    <SortableHeader testid="sort-price" label="Prezzo" sortKey="price" sortConfig={sort} setSortConfig={setSort} />
                    <ColumnFilter testid="filter-price" values={uniques.price} selected={filters.price} onChange={setColFilter("price")} />
                  </span>
                </th>
                <th className="px-3 py-3 label-eyebrow text-center bg-neutral-50">
                  <span className="inline-flex items-center gap-1 justify-center w-full">
                    <SortableHeader testid="sort-value" label="Valore" sortKey="value" sortConfig={sort} setSortConfig={setSort} />
                    <ColumnFilter testid="filter-value" values={uniques.value} selected={filters.value} onChange={setColFilter("value")} />
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 ? (
                <tr><td colSpan="12" className="px-4 py-12 text-center text-neutral-500">Nessuna riga di giacenza corrisponde ai filtri.</td></tr>
              ) : filteredRows.map((r, idx) => {
                const isLow = Number(r.stock || 0) <= Number(r.min_stock || 0);
                const clickHistory = r.serial_number
                  ? () => setHistoryModal({ mode: "serial", serial: r.serial_number })
                  : () => setHistoryModal({ mode: "product", productId: r.product_id, productName: r.name, productSku: r.sku });
                return (
                  <tr
                    key={`${r.row_id}:${idx}`}
                    data-testid={`inv-row-${r.row_id}`}
                    onClick={clickHistory}
                    className="border-b border-neutral-100 hover:bg-[#002FA7]/5 transition-colors cursor-pointer"
                    title="Clicca per vedere lo storico"
                  >
                    <td className="px-3 py-3 font-mono text-xs whitespace-nowrap">{r.sku}</td>
                    <td className="px-3 py-3 font-mono text-xs text-neutral-600 whitespace-nowrap">{r.manufacturer_code || "—"}</td>
                    <td className="px-3 py-3 font-medium">{r.name}</td>
                    <td className="px-3 py-3 font-mono text-xs whitespace-nowrap">
                      {r.serial_number
                        ? <span className="font-semibold">{r.serial_number}</span>
                        : <span className="text-neutral-400">— sfuso —</span>}
                    </td>
                    <td className="px-3 py-3 text-neutral-600 text-xs whitespace-nowrap">{r.category_name || "—"}</td>
                    <td className="px-3 py-3 text-neutral-600 text-xs whitespace-nowrap">{r.supplier_name || "—"}</td>
                    <td className="px-3 py-3 text-xs whitespace-nowrap">
                      <span className="inline-flex items-center gap-1">
                        {r.warehouse_type === "mobile" ? <Truck size={12} className="text-amber-600" weight="bold" /> : <Buildings size={12} className="text-[#002FA7]" weight="bold" />}
                        {r.warehouse_name}
                      </span>
                    </td>
                    <td className={`px-3 py-3 text-right font-mono font-semibold whitespace-nowrap ${isLow ? "text-red-600" : ""}`}>{r.stock}</td>
                    <td className="px-3 py-3 text-center font-mono text-xs text-neutral-600 whitespace-nowrap">{r.unit_of_measure || "pz"}</td>
                    <td className="px-3 py-3 text-right font-mono text-neutral-500 whitespace-nowrap">{r.min_stock}</td>
                    <td className="px-3 py-3 text-right font-mono whitespace-nowrap">€ {Number(r.price || 0).toFixed(2)}</td>
                    <td className="px-3 py-3 text-right font-mono font-semibold whitespace-nowrap">€ {Number(r.value || 0).toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
            {filteredRows.length > 0 && (
              <tfoot>
                <tr className="bg-neutral-100 border-t-2 border-[#002FA7] text-neutral-900">
                  <td colSpan="7" className="px-3 py-3 label-eyebrow text-neutral-500 whitespace-nowrap">Totale ({filteredRows.length} righe)</td>
                  <td className="px-3 py-3 text-right font-mono font-bold whitespace-nowrap">{totals.stock}</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td className="px-3 py-3 text-right font-mono font-bold text-[#002FA7] whitespace-nowrap">€ {totals.value.toFixed(2)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Scanner Modal */}
      {scannerOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 md:p-4">
          <div className="bg-white border border-neutral-200 shadow-xl w-full max-w-xl max-h-[95vh] overflow-y-auto" data-testid="scanner-modal">
            <div className="p-4 md:p-6 border-b border-neutral-200 flex items-center justify-between">
              <div>
                <div className="label-eyebrow mb-1">Lookup Seriale</div>
                <h2 className="font-display font-bold text-xl tracking-tight flex items-center gap-2">
                  <Barcode size={22} weight="bold" className="text-[#002FA7]" />
                  Scanner Ricerca
                </h2>
              </div>
              <button data-testid="close-scanner" onClick={() => setScannerOpen(false)} className="p-1.5 hover:bg-neutral-100 transition-colors">
                <XIcon size={18} />
              </button>
            </div>
            <div className="p-4 md:p-6 space-y-4">
              <ScannerInput
                onScan={doScan}
                autoFocus
                label="Scansiona un numero seriale"
                placeholder="Scansiona con scanner USB o digita..."
                testid="lookup-scanner"
              />

              {scanLoading && (
                <div className="text-sm text-neutral-500 py-4 text-center">Ricerca in corso...</div>
              )}

              {scanResult && scanResult.error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-4">
                  <div className="font-mono font-semibold mb-1">{scanResult.code}</div>
                  <div>{scanResult.error}</div>
                </div>
              )}

              {scanResult && !scanResult.error && scanResult.serial && (
                <div className="border border-neutral-200 divide-y divide-neutral-100" data-testid="scan-result">
                  <div className="p-4 bg-neutral-50">
                    <div className="label-eyebrow mb-1">Seriale</div>
                    <div className="font-mono font-black text-lg tracking-tight">{scanResult.serial.serial_number}</div>
                    <div className="mt-1 flex items-center gap-2">
                      <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${scanResult.serial.status === "in_stock" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                        {scanResult.serial.status === "in_stock" ? "IN GIACENZA" : "USCITO"}
                      </span>
                    </div>
                  </div>

                  {scanResult.product && (
                    <div className="p-4">
                      <div className="label-eyebrow mb-1">Prodotto</div>
                      <div className="font-semibold text-sm">{scanResult.product.name}</div>
                      <div className="text-xs font-mono text-neutral-500">SKU: {scanResult.product.sku}</div>
                    </div>
                  )}

                  <div className="p-4">
                    <div className="label-eyebrow mb-1">Posizione attuale</div>
                    {scanResult.warehouse ? (
                      <div className="flex items-center gap-2 text-sm">
                        {scanResult.warehouse.type === "mobile"
                          ? <Truck size={16} className="text-amber-600" weight="bold" />
                          : <Buildings size={16} className="text-[#002FA7]" weight="bold" />}
                        <span className="font-medium">{scanResult.warehouse.name}</span>
                        {scanResult.warehouse.owner_name && <span className="text-neutral-500 text-xs">· {scanResult.warehouse.owner_name}</span>}
                      </div>
                    ) : (
                      <div className="text-sm text-neutral-500">— Uscito dal magazzino</div>
                    )}
                  </div>

                  <div className="p-4">
                    <div className="label-eyebrow mb-2">Storico movimenti ({scanResult.movements?.length || 0})</div>
                    <ol className="space-y-2">
                      {(scanResult.movements || []).map((m) => {
                        const iconMap = { entry: ArrowDown, exit: ArrowUp, return: ArrowUUpLeft, transfer: ArrowsLeftRight };
                        const labelMap = { entry: "Entrata", exit: "Uscita", return: "Reso", transfer: "Trasferim." };
                        const Icon = iconMap[m.type] || ArrowDown;
                        return (
                          <li key={m.id} className="flex items-start gap-3 text-xs border-l-2 border-[#002FA7] pl-3 py-1">
                            <Icon size={14} weight="bold" className="mt-0.5 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold">{labelMap[m.type]}</span>
                                <span className="text-neutral-400 font-mono">{new Date(m.created_at).toLocaleString("it-IT")}</span>
                              </div>
                              <div className="text-neutral-600">
                                {m.from_warehouse_name || "—"} → {m.to_warehouse_name || "—"}
                              </div>
                              {m.reference && <div className="text-neutral-500">Rif: {m.reference}</div>}
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                  </div>
                </div>
              )}
            </div>
            <div className="p-4 md:p-6 pt-0 flex justify-end border-t border-neutral-200 mt-4">
              <button onClick={() => setScannerOpen(false)} className="px-4 py-2 text-sm border border-neutral-300 hover:bg-neutral-100 transition-colors">Chiudi</button>
            </div>
          </div>
        </div>
      )}

      <MovementHistoryModal
        open={!!historyModal}
        onClose={() => setHistoryModal(null)}
        mode={historyModal?.mode}
        productId={historyModal?.productId}
        productName={historyModal?.productName}
        productSku={historyModal?.productSku}
        serial={historyModal?.serial}
      />
    </div>
  );
}
